// v642.4: Social dropdown — Guild · Fellowship · Online roster.
// Replaces three separate bottom-HUD buttons with a single dropdown
// pill, freeing space and giving social actions a unified entry.
// v642.14: portal the menu to <body> and position from the trigger's
// bounding rect, so it can never be painted under sibling stacking
// contexts (chat panel, log panel, etc.).

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const SocialDropdown = ({
  onOpenGuild,
  onOpenFellowship,
  onToggleOnline,
  onlineCount = 0,
  guildBadge = 0,
  fellowshipBadge = 0,
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && wrapRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Measure trigger and pin the menu just above it, right-aligned.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined;
    const measure = () => {
      const r = triggerRef.current.getBoundingClientRect();
      setPos({
        bottom: Math.max(8, window.innerHeight - r.top + 6),
        right: Math.max(8, window.innerWidth - r.right),
      });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  const totalBadge = (guildBadge || 0) + (fellowshipBadge || 0) + (onlineCount > 0 ? 0 : 0);

  return (
    <div ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={triggerRef}
        className="dm-action-btn"
        onClick={() => setOpen(o => !o)}
        title="Social (Guild · Fellowship · Online)"
        style={{ position: 'relative' }}
      >
        👥
        {totalBadge > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4,
            background: '#e07070', color: '#fff', fontSize: 10,
            borderRadius: 8, minWidth: 16, height: 16, padding: '0 4px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700,
          }}>{totalBadge}</span>
        )}
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            bottom: pos.bottom,
            right: pos.right,
            minWidth: 180,
            background: 'rgba(18, 24, 36, 0.97)',
            border: '1px solid rgba(120, 170, 220, 0.45)',
            borderRadius: 6,
            boxShadow: '0 6px 18px rgba(0,0,0,0.5)',
            zIndex: 1000,
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <button
            type="button"
            onClick={() => { setOpen(false); if (onOpenGuild) onOpenGuild(); }}
            style={socialItemStyle}
          >
            <span>⚔️ Guild</span>
            {guildBadge > 0 && <span style={badgeStyle}>{guildBadge}</span>}
          </button>
          <button
            type="button"
            onClick={() => { setOpen(false); if (onOpenFellowship) onOpenFellowship(); }}
            style={socialItemStyle}
          >
            <span>🤝 Fellowship</span>
            {fellowshipBadge > 0 && <span style={badgeStyle}>{fellowshipBadge}</span>}
          </button>
          {onToggleOnline && (
            <button
              type="button"
              onClick={() => { setOpen(false); onToggleOnline(); }}
              style={socialItemStyle}
            >
              <span>👥 Online</span>
              {onlineCount > 0 && <span style={{ ...badgeStyle, background: '#2c7a3c' }}>{onlineCount}</span>}
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

const socialItemStyle = {
  padding: '7px 10px',
  background: 'transparent',
  border: 'none',
  color: '#dceeff',
  fontSize: 13,
  textAlign: 'left',
  cursor: 'pointer',
  borderRadius: 4,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
};

const badgeStyle = {
  background: '#e07070',
  color: '#fff',
  fontSize: 10,
  borderRadius: 8,
  minWidth: 16,
  height: 16,
  padding: '0 5px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
};

export default SocialDropdown;
